import {
  HOME_PATIENT_STATUS,
  SessionCarouselStates,
  EXERCISE_PARAMS_MODES,
} from '@/constants/constants';
import { getTotalTimeSeconds } from '@/utils/helpers';

/**
 * One prescription exercise (GET /sessions/current, GET /onboarding/patient) → the shape the
 * patient exercise list (FP4) renders: name, thumbnail, video and its 2 key parameters.
 */
export const mapPrescriptionExercise = ex => {
  const type = (ex.type && String(ex.type).toUpperCase()) || 'BASED_ON_REP';
  const rep = ex.basedOnRep && typeof ex.basedOnRep === 'object' ? ex.basedOnRep : {};
  const time = ex.basedOnTime && typeof ex.basedOnTime === 'object' ? ex.basedOnTime : {};
  const single =
    ex.basedOnSingleRep &&
    typeof ex.basedOnSingleRep === 'object' &&
    !Array.isArray(ex.basedOnSingleRep)
      ? ex.basedOnSingleRep
      : {};
  const isTime = type === 'BASED_ON_TIME';
  const isSingleRep =
    type === 'BASED_ON_SINGLE_REP' ||
    (Object.keys(single).length > 0 && (single.sets != null || single.holdDuration != null));

  let params = [];
  if (isSingleRep) {
    const sets = single.sets ?? rep.sets ?? 1;
    const holdSecs = single.holdDuration ?? rep.holdDuration ?? 0;
    params = [
      { label: 'sets', value: Number(sets) || 1 },
      { label: 'secs', value: Number(holdSecs) || 0 },
    ];
  } else if (isTime) {
    const sets = time.sets ?? 1;
    const setDuration = time.setDuration ?? 0;
    params = [
      { label: 'sets', value: Number(sets) || 1 },
      { label: 'set_duration', value: Number(setDuration) || 0 },
    ];
  } else {
    const sets = rep.sets ?? 1;
    const reps = rep.reps ?? 0;
    params = [
      { label: 'sets', value: Number(sets) || 1 },
      { label: 'reps', value: Number(reps) || 0 },
    ];
  }

  const paramMode =
    ex.paramMode ??
    (isTime
      ? EXERCISE_PARAMS_MODES.BASED_ON_TIME
      : isSingleRep
        ? EXERCISE_PARAMS_MODES.SINGLE_REP
        : EXERCISE_PARAMS_MODES.BASED_ON_REP);

  return {
    ...ex,
    type,
    paramMode,
    thumbnailPath: ex.image,
    exerciseName: ex.name,
    VideoPath: ex.video,
    note: ex.note ?? null,
    voiceMessagePath: ex.voiceMessagePath ?? ex.voice ?? null,
    params,
  };
};

export function mapHomePatientResponse(data) {
  const raw = data?.data;
  if (!raw || !raw.session?.prescription) {
    return null;
  }
  const getEquipmentNames = prescription => {
    const equipmentGroup = (prescription?.tags || []).find(group => {
      const name = String(group?.name || '')
        .trim()
        .toLowerCase();
      return name === 'equipment' || name === 'المعدات';
    });

    const equipmentFromTags = (equipmentGroup?.tags || [])
      .map(tag => tag?.name)
      .filter(name => typeof name === 'string' && name.trim() !== '');

    if (equipmentFromTags.length > 0) {
      return equipmentFromTags;
    }

    return (prescription?.equipment || [])
      .map(item => (typeof item === 'string' ? item : item?.name))
      .filter(name => typeof name === 'string' && name.trim() !== '');
  };
  const session = raw.session;
  const prescription = session.prescription;
  const adherence = raw.adherence || {};
  const weeksAdherence = adherence.weeks || {};
  const prescriptionAdherence = adherence.prescription || {};

  const weekDays = weeksAdherence.days || [];
  const weekSummary = weeksAdherence.summary || {};
  const prescriptionWeeks = prescriptionAdherence.weeks || [];
  const prescriptionSummary = prescriptionAdherence.summary || {};

  const days = weekDays.map(d => ({
    date: d.date,
    completedSessions: d.performedSessions ?? 0,
    totalSessions: d.totalSessions ?? 0,
  }));

  const weeks = prescriptionWeeks.map((w, index) => ({
    weekNumber: typeof w.week === 'string' ? parseInt(w.week, 10) || index + 1 : index + 1,
    adherence: w.adherence !== null && w.adherence !== undefined ? w.adherence : null,
    isCurrentWeek: false,
  }));
  const lastCompletedIndex = prescriptionWeeks.findIndex(w => w.adherence === null);
  const currentWeekIndex =
    lastCompletedIndex === -1 ? weeks.length - 1 : Math.max(0, lastCompletedIndex - 1);
  if (weeks[currentWeekIndex]) {
    weeks[currentWeekIndex].isCurrentWeek = true;
  }

  const progressTracker = {
    frequency: {
      day: prescription.days ?? 1,
      week: prescription.weeks ?? 1,
    },
    days,
    weeks,
    progressBar: {
      totalSessions: weekSummary.totalSessions ?? 0,
      completedSessions: weekSummary.performedSessions ?? 0,
    },
    ...(prescription.status != null && String(prescription.status).trim() !== ''
      ? { prescriptionStatus: prescription.status }
      : {}),
  };

  const exercises = (prescription.exercises || []).map(mapPrescriptionExercise);

  const etaFromPrescription = Number(prescription.eta);
  const etaSecondsFromApi =
    Number.isFinite(etaFromPrescription) && etaFromPrescription > 0 ? etaFromPrescription : 0;
  const estimatedFromExercises = getTotalTimeSeconds(exercises);
  const prescribedSessionEstimatedDuration = Math.max(etaSecondsFromApi, estimatedFromExercises);

  const currentWeekAdherence =
    weeks[currentWeekIndex]?.adherence != null ? weeks[currentWeekIndex].adherence : 0;

  const sessionVariant =
    raw.state === HOME_PATIENT_STATUS.INCOMPLETE_DAY
      ? SessionCarouselStates.INCOMPLETE_DAY
      : raw.state === HOME_PATIENT_STATUS.COMPLETE_WEEK
        ? currentWeekAdherence >= 76
          ? SessionCarouselStates.COMPLETE_WEEK_FOURTH_TIER
          : currentWeekAdherence >= 51
            ? SessionCarouselStates.COMPLETE_WEEK_THIRD_TIER
            : currentWeekAdherence >= 26
              ? SessionCarouselStates.COMPLETE_WEEK_SECOND_TIER
              : SessionCarouselStates.COMPLETE_WEEK_FIRST_TIER
        : SessionCarouselStates.COMPLETE_DAY;

  const completedSessions = session.completed
    ? session.order
    : Math.max(0, (session.order || 1) - 1);

  return {
    hasActivePrescription: true,
    firstName: raw.firstName ?? '',
    state: raw.state,
    sessionVariant,
    progressTracker,
    prescriptionWeek: weeks[currentWeekIndex]?.weekNumber ?? currentWeekIndex + 1,
    prescriptionDuration: prescriptionSummary.totalWeeks ?? 0,
    prescription: {
      id: prescription.id,
      prescriptionName: prescription.name,
      therapist: prescription.therapist ?? null,
      equipment: getEquipmentNames(prescription) || [],
      totalSessions: session.total ?? prescription.days ?? 0,
      completedSessions,
      sessionDayOrder: session.order,
      sessionPrescriptionOrder: session.prescriptionOrder,
      status: session.completed ? 'complete' : 'incomplete',
      sessionId: session.sessionId ?? null,
      sessionStatus: session.status ?? null,
      prescribedSession: {
        estimatedDuration: prescribedSessionEstimatedDuration,
        exercises,
      },
    },
    prescriptionSummary: {
      performedWeeks: prescriptionSummary.performedWeeks ?? 0,
      totalWeeks: prescriptionSummary.totalWeeks ?? 0,
    },
  };
}
